import { useState, FormEvent, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { Product } from '../../stores/appReportsStore'
import EditOptionProductCard from './edit/EditOptionProductCard'
import { useTranslation } from 'react-i18next'
import { disabledButtonClass, greenButtonClass } from '../../styling/stylingHelper'

export interface SourcingSearchProps {
  parentProduct: Product
  searchOptions: Product[]
  loading: boolean
  onSearch: (term: string) => Promise<Product[]>
  onUpdateOptions: (opts: Product[]) => void
  onSelect: (opt: Product) => void
}

export default function SourcingSearch({
  parentProduct,
  searchOptions,
  loading,
  onSearch,
  onUpdateOptions,
  onSelect,
}: SourcingSearchProps) {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')

  

  const sortedHits = useMemo(() => {
    const { unitPrice, standardUnit } = parentProduct
    const isPiece = ['stuk','piece'].includes(standardUnit)
    const isGml   = ['g','ml'].includes(standardUnit)

    return [...searchOptions].sort((a, b) => {
      const cmp = (p: Product) => {
        const comparable = (isPiece && ['stuk','piece'].includes(p.standardUnit))
                        || (isGml   && ['g','ml'].includes(p.standardUnit))
        return comparable
          ? (p.unitPrice - unitPrice) / unitPrice * 100
          : null
      }
      const da = cmp(a), db = cmp(b)
      const ga = da == null ? 1 : da <= 0 ? 0 : 2
      const gb = db == null ? 1 : db <= 0 ? 0 : 2
      if (ga !== gb) return ga - gb
      if (ga === 1)  return a.unitPrice - b.unitPrice
      return da! - db!
    })
  }, [searchOptions, parentProduct])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!term.trim() || loading) return
    const hits = await onSearch(term.trim())
    const map = new Map<string, Product>()
    ;[...searchOptions, ...hits].forEach(p => map.set(p.articleId, p))
    onUpdateOptions(Array.from(map.values()))
    setTerm('')
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
        <input
          className="flex-1 p-2 border rounded-2xl"
          value={term}
          onChange={e => setTerm(e.target.value)}
          placeholder={t('sourcing.searchPlaceholder')}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={!term.trim() || loading}
          className={`px-4 py-2 bg-secondary dark:bg-secondary-dark rounded-xl flex items-center gap-2 disabled:cursor-not-allowed
            ${!term.trim()  
              ? disabledButtonClass 
              : greenButtonClass
            }
            transition
            `}
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('sourcing.searchButton')}
        </button>
      </form>

      {sortedHits.length > 0 && (
        <div className="space-y-2">
          {sortedHits.map(hit => (
            <div
              key={hit.articleId}
              onClick={() => onSelect(hit)}
              className="cursor-pointer"
            >
              <EditOptionProductCard
                parentProduct={parentProduct}
                product={hit}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
